// Password reset by email (M7 slice 3; spec/dashboard.md section 3.1; spec/api-conventions.md section 21.3): `/forgot-password`
// asks `POST /v1/auth/password-reset:request` and always says the same thing (the API never tells whether an address has an
// account); `/reset-password?token=…` sets the new password through `POST /v1/auth/password-reset:complete`, which signs the
// caller in (every other session is revoked), then goes to `/`.
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ApiError } from '../api/client';
import { ops } from '../api/operations';
import { useAuth } from '../auth/AuthContext';
import { SignInBrand } from './SignIn';

export const RESET_SENT = 'If that address has an account, a mail is on its way.';

const asError = (e: unknown) => (e instanceof ApiError ? e : new ApiError(0, 'internal_error', 'Something went wrong.', ''));

export function ForgotPassword() {
  const { client } = useAuth();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await client.call(ops.requestPasswordReset, { body: { email: email.trim() }, auth: false });
      setSent(true);
    } catch (err) {
      setError(asError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <form onSubmit={(e) => void submit(e)} className="card" aria-labelledby="forgot-title">
        <SignInBrand />
        <h1 id="forgot-title">Reset your password</h1>
        {sent ? (
          <p className="lede" role="status" data-testid="reset-sent">
            {RESET_SENT} The link in it works for one hour.
          </p>
        ) : (
          <>
            <p className="lede">Enter the email of your account; a link to set a new password is mailed to it.</p>
            <label>
              Email
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required />
              {error?.fieldMessage('email') && <span className="field-error">{error.fieldMessage('email')}</span>}
            </label>
            {error && !error.details?.length && (
              <p className="error" role="alert">
                {error.message}
                {error.requestId ? ` (request ${error.requestId})` : ''}
              </p>
            )}
            <button type="submit" disabled={busy}>
              {busy ? 'Sending…' : 'Send the link'}
            </button>
          </>
        )}
        <div className="links">
          <p>
            <Link to="/sign-in">Back to sign in</Link>
          </p>
        </div>
      </form>
    </div>
  );
}

export function ResetPassword() {
  const { client, adoptSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const mismatch = repeat.length > 0 && repeat !== password;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError(null);
    try {
      const session = await client.data(ops.completePasswordReset, { body: { token, newPassword: password }, auth: false });
      adoptSession(session);
      navigate('/', { replace: true });
    } catch (err) {
      setError(asError(err));
    } finally {
      setBusy(false);
    }
  };

  const dead = error?.status === 404;
  return (
    <div className="signin">
      <form onSubmit={(e) => void submit(e)} className="card" aria-labelledby="reset-title">
        <SignInBrand />
        <h1 id="reset-title">Set a new password</h1>
        <p className="lede">Every other session of the account is signed out.</p>
        {!token ? (
          <p className="error" role="alert">
            This link carries no reset token. <Link to="/forgot-password">Ask for a new link</Link>.
          </p>
        ) : (
          <>
            <label>
              New password
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={10} required />
              {error?.fieldMessage('newPassword') && <span className="field-error">{error.fieldMessage('newPassword')}</span>}
            </label>
            <label>
              Repeat the password
              <input type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)} autoComplete="new-password" required aria-invalid={mismatch || undefined} />
            </label>
            {mismatch && <span className="field-error">The passwords differ.</span>}
            {error && !error.details?.length && (
              <p className="error" role="alert">
                {dead ? (
                  <>
                    This reset link is unknown, used or expired. <Link to="/forgot-password">Ask for a new link</Link>.
                  </>
                ) : (
                  `${error.message}${error.requestId ? ` (request ${error.requestId})` : ''}`
                )}
              </p>
            )}
            <button type="submit" disabled={busy || mismatch}>
              {busy ? 'Saving…' : 'Set the password'}
            </button>
            <div className="links">
              <p>
                <Link to="/sign-in">Back to sign in</Link>
              </p>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
